/**
 * No Spend Weekend — Firestore persistence + native bridge.
 *
 * Stores the user's chosen blocklist (preset IDs + custom domains + the iOS
 * FamilyActivityPicker token) so the settings survive reinstall and can be
 * replayed into the native blocker. Also wraps the Capacitor plugin so the
 * UI layer doesn't need to know whether we're on iOS, Android, or web.
 */

import { doc, getDoc, onSnapshot, serverTimestamp, setDoc, type Unsubscribe } from 'firebase/firestore';
import { db } from '@shared/services/firebase';
import { recordFeatureUseEvent } from '@shared/services/analyticsService';
import { Capacitor } from '@capacitor/core';

import ShoppingBlocker, {
  type BlockingPermissionState,
  type BlockingStatus,
} from '../plugins/ShoppingBlockerPlugin';
import {
  collectAndroidPackageNames,
  collectDomains,
} from './shoppingBlockerPresets';
import { stabilizeAuthorization } from './shoppingBlockerAuth';
import { calculateSessionSavings } from './quietDaysSavings';
import type { Transaction } from '@shared/types';

export type BlockWindow = 'weekend' | '24h' | '48h' | 'custom';

// Event schedule (derived from the noSpendEvents Firestore collection) lives in
// its own pure module; re-exported here so existing importers keep working.
export {
  isOfficialWindowActive,
  getNextOfficialWindow,
  getPastEventStarts,
  daysUntilWindow,
  isWithinEventWeek,
  type OfficialNoSpendWindow,
} from './noSpendEventSchedule';

export interface NoSpendSettings {
  /** Preset app IDs (see shoppingBlockerPresets.ts). */
  selectedAppIds: string[];
  /** User-added domains (without scheme, e.g. "example.com"). */
  customDomains: string[];
  /** Opaque token from iOS FamilyActivityPicker. */
  iosPickedAppsToken: string | null;
  /** Display-only: counts stored alongside the token so the UI can show them. */
  iosPickedCounts: { applications: number; categories: number; webDomains: number } | null;
  /** Last active block, if any. */
  activeSession: {
    startedAt: number;
    endsAt: number;
    reason: string;
  } | null;
  /** User preference: show iOS Live Activity when a block session starts. */
  lockScreenReminderEnabled?: boolean;
  /** Optional custom motivational line. When set, it replaces the rotating
   *  quotes/tips shown in the Live Activity for this user's Shopping Breaks. */
  motivationalText?: string;
  /** Sessions that ran to their full window (never reset — feeds the Pause wins strip). */
  sessionsCompleted?: number;
  /** Free-tier opt-in to the next official app-wide No-Spend Weekend. `eventId`
   *  ties the opt-in back to its noSpendEvents doc so the server window-start
   *  push can skip events that were since deleted/canceled (older opt-ins may
   *  lack it and are matched by `start`). */
  officialWindowOptIn?: { start: number; end: number; eventId?: string } | null;
  /** Event-start timestamps (ms) the user opted into — drives the home-card
   *  3-strike eligibility rule. Capped to the most recent few. */
  eventOptIns?: number[];
  /** Event-start timestamps (ms) whose home countdown card was dismissed. */
  dismissedEventCards?: number[];
  /** Sessions that ran their full window. `savings` stays null until settled
   *  (72h after end, so bank transactions have posted), then holds the raw
   *  baseline-minus-actual dollars (can be negative). */
  completedSessions?: Array<{ startedAt: number; endsAt: number; savings: number | null }>;
  /** Cumulative Quiet Days savings — sum of each settled session's savings
   *  floored at $0. Feeds the Pause page wins strip. */
  totalSavings?: number;
  /** Session-start timestamps (ms) whose post-event recap card was dismissed. */
  dismissedRecaps?: number[];
  updatedAt?: unknown;
}

const DEFAULT_SETTINGS: NoSpendSettings = {
  selectedAppIds: [],
  customDomains: [],
  iosPickedAppsToken: null,
  iosPickedCounts: null,
  activeSession: null,
};

function settingsDoc(uid: string) {
  return doc(db, 'userContent', uid, 'blocking', 'noSpendWeekend');
}

export async function loadSettings(uid: string): Promise<NoSpendSettings> {
  const snap = await getDoc(settingsDoc(uid));
  if (!snap.exists()) return { ...DEFAULT_SETTINGS };
  return { ...DEFAULT_SETTINGS, ...(snap.data() as NoSpendSettings) };
}

export function subscribeToSettings(
  uid: string,
  callback: (settings: NoSpendSettings) => void
): Unsubscribe {
  return onSnapshot(settingsDoc(uid), (snap) => {
    if (!snap.exists()) {
      callback({ ...DEFAULT_SETTINGS });
      return;
    }
    callback({ ...DEFAULT_SETTINGS, ...(snap.data() as NoSpendSettings) });
  });
}

export async function saveSettings(
  uid: string,
  partial: Partial<NoSpendSettings>
): Promise<void> {
  await setDoc(
    settingsDoc(uid),
    { ...partial, updatedAt: serverTimestamp() },
    { merge: true }
  );
}

// ---- Permission helpers ----

export async function getAuthorization(): Promise<BlockingPermissionState> {
  // iOS can report a stale `notDetermined` on cold launch before Screen Time
  // authorization finishes loading; stabilize the read so the grant button is
  // not shown to users who already granted access (card WF5x4If150tnYYcbDsuU).
  return stabilizeAuthorization(
    () => ShoppingBlocker.getAuthorizationStatus(),
    { stabilize: Capacitor.getPlatform() === 'ios' }
  );
}

export async function requestAuthorization(): Promise<BlockingPermissionState> {
  return ShoppingBlocker.requestAuthorization();
}

export async function openAccessibilitySettings(): Promise<void> {
  return ShoppingBlocker.openAccessibilitySettings();
}

export async function openOverlaySettings(): Promise<void> {
  return ShoppingBlocker.openOverlaySettings();
}

// ---- Blocking lifecycle ----

export interface StartBlockArgs {
  uid: string;
  settings: NoSpendSettings;
  endsAt: number;
  reason?: string;
  /** Individually-picked app count for Live Activity / notification display. */
  appCount?: number;
  /** iOS Screen Time category count — shown separately, never merged into apps. */
  categoryCount?: number;
  /** Total site/domain count for Live Activity / notification display. */
  siteCount?: number;
  /** When true (iOS + Remote Config), native starts a Live Activity. */
  showLiveActivity?: boolean;
  /** When true (Android + Remote Config), native shows an ongoing notification. */
  showOngoingNotification?: boolean;
  /** User's custom motivational line. When set, native shows it in the Live
   *  Activity instead of the rotating quotes/tips. */
  motivationalText?: string;
  /** Admin-authored rotating lines for the joined event's Live Activity. Native
   *  picks one (falls back to a built-in seed list when empty). */
  liveActivityMessages?: string[];
}

export async function startBlocking({
  uid,
  settings,
  endsAt,
  reason = 'Shopping Break',
  appCount,
  categoryCount,
  siteCount,
  showLiveActivity = false,
  showOngoingNotification = false,
  motivationalText,
  liveActivityMessages,
}: StartBlockArgs): Promise<BlockingStatus> {
  const webDomains = collectDomains(settings.selectedAppIds, settings.customDomains);
  const androidPackageNames = collectAndroidPackageNames(settings.selectedAppIds);

  const status = await ShoppingBlocker.startBlocking({
    pickedAppsToken: settings.iosPickedAppsToken ?? undefined,
    androidPackageNames,
    webDomains,
    endsAt,
    reason,
    appCount,
    categoryCount,
    siteCount,
    showLiveActivity,
    showOngoingNotification,
    motivationalText: motivationalText?.trim() || undefined,
    liveActivityMessages: liveActivityMessages?.map(m => m.trim()).filter(Boolean),
  });

  if (status.active) {
    await saveSettings(uid, {
      activeSession: {
        startedAt: Date.now(),
        endsAt,
        reason,
      },
    });
    recordFeatureUseEvent(uid, 'no_spend_weekend', 'start', { oncePerSession: false }).catch(() => {});
  }
  return status;
}

export async function stopBlocking(uid: string): Promise<void> {
  await ShoppingBlocker.stopBlocking();
  await saveSettings(uid, { activeSession: null });
  recordFeatureUseEvent(uid, 'no_spend_weekend', 'stop', { oncePerSession: false }).catch(() => {});
}

/**
 * If the active session's window has fully elapsed, count it as completed and
 * clear it. Ending early via stopBlocking() does not count — only sessions that
 * ran their full course. Safe to call opportunistically (no-op otherwise).
 */
export async function completeExpiredSession(
  uid: string,
  settings: NoSpendSettings
): Promise<void> {
  if (!settings.activeSession || settings.activeSession.endsAt > Date.now()) return;
  const { startedAt, endsAt } = settings.activeSession;
  // Record the window so savings can be settled later (transactions take days
  // to post). Cap the list as a corrupted-data guard.
  const completedSessions = [
    ...(settings.completedSessions ?? []),
    { startedAt, endsAt, savings: null },
  ].slice(-24);
  await saveSettings(uid, {
    activeSession: null,
    sessionsCompleted: (settings.sessionsCompleted ?? 0) + 1,
    completedSessions,
  });
  recordFeatureUseEvent(uid, 'no_spend_weekend', 'complete', { oncePerSession: false }).catch(() => {});
}

/** Transactions need ~3 days to post before a session's spend is trustworthy. */
const SAVINGS_SETTLE_DELAY_MS = 72 * 60 * 60 * 1000;

/**
 * Compute savings for completed sessions whose post-window settling period has
 * elapsed and whose savings are still null. One Firestore write; no-op when
 * nothing is ready. Cumulative `totalSavings` floors each session at $0 —
 * negative sessions are kept honestly on the record but never subtract.
 */
export async function settleSessionSavings(
  uid: string,
  settings: NoSpendSettings,
  transactions: Transaction[]
): Promise<void> {
  const sessions = settings.completedSessions ?? [];
  const cutoff = Date.now() - SAVINGS_SETTLE_DELAY_MS;
  if (!sessions.some(s => s.savings === null && s.endsAt <= cutoff)) return;

  let addedSavings = 0;
  const settled = sessions.map(s => {
    if (s.savings !== null || s.endsAt > cutoff) return s;
    const { savings } = calculateSessionSavings(s, transactions);
    addedSavings += Math.max(0, savings);
    return { ...s, savings };
  });
  await saveSettings(uid, {
    completedSessions: settled,
    totalSavings: (settings.totalSavings ?? 0) + addedSavings,
  });
}

export async function refreshStatus(): Promise<BlockingStatus> {
  return ShoppingBlocker.getBlockingStatus();
}

export async function areLiveActivitiesEnabled(): Promise<boolean> {
  if (Capacitor.getPlatform() !== 'ios') return false;
  const { enabled } = await ShoppingBlocker.areLiveActivitiesEnabled();
  return enabled;
}

export async function areNotificationsEnabled(): Promise<boolean> {
  if (Capacitor.getPlatform() !== 'android') return false;
  const { enabled } = await ShoppingBlocker.areNotificationsEnabled();
  return enabled;
}

export async function openNotificationSettings(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;
  return ShoppingBlocker.openNotificationSettings();
}

// ---- iOS picker ----

export async function pickAppsOnIOS(uid: string, currentToken?: string | null): Promise<void> {
  if (Capacitor.getPlatform() !== 'ios') return;
  // Pass the saved token so the picker re-opens with the prior selection.
  const picked = await ShoppingBlocker.pickApps(
    currentToken ? { currentToken } : undefined
  );
  await saveSettings(uid, {
    iosPickedAppsToken: picked.token,
    iosPickedCounts: {
      applications: picked.applicationCount,
      categories: picked.categoryCount,
      webDomains: picked.webDomainCount,
    },
  });
}

// ---- Preset window helpers ----

/**
 * Compute the end timestamp for a given window, starting from `now`.
 * - "weekend": next Sunday 23:59 local (or this Sunday if already on Sat/Sun)
 * - "24h"/"48h": now + N hours
 * - "custom": caller passes its own endsAt
 */
export function computeEndsAt(window: BlockWindow, now: Date = new Date()): number {
  if (window === '24h') return now.getTime() + 24 * 60 * 60 * 1000;
  if (window === '48h') return now.getTime() + 48 * 60 * 60 * 1000;
  if (window === 'weekend') {
    const end = new Date(now);
    const day = end.getDay();
    // 0=Sun, 6=Sat. End = Sunday 23:59 local.
    const daysUntilSunday = day === 0 ? 0 : 7 - day;
    end.setDate(end.getDate() + daysUntilSunday);
    end.setHours(23, 59, 0, 0);
    if (end.getTime() <= now.getTime()) {
      end.setDate(end.getDate() + 7);
    }
    return end.getTime();
  }
  // 'custom' — caller should supply their own value; fall back to +24h.
  return now.getTime() + 24 * 60 * 60 * 1000;
}
